import { useCallback, useEffect, useState } from 'react';
import { usePromptGroups } from '../usePromptGroups';
import { PromptGroup } from '@server/prompts';
import isEqual from 'lodash/isEqual';

export const usePromptLibraryCard = ({ promptGroup }: { promptGroup: PromptGroup }) => {
  const [promptGroupInEdit, setPromptGroupInEdit] = useState<PromptGroup | null>(null);
  const [showNameErrorState, setShowNameErrorState] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [initialCardState, setInitialCardState] = useState<PromptGroup | null>(null);

  const { createPromptGroup, updatePromptGroup, removeEmptyPromptGroup, removePromptGroup } =
    usePromptGroups();

  useEffect(() => {
    if (promptGroupInEdit && initialCardState) return;
    if (promptGroupInEdit?.id === '') return;
    setInitialCardState(promptGroupInEdit);
  }, [promptGroupInEdit, initialCardState]);

  const onEdit = useCallback((key: string, value: string) => {
    if (key === 'title' && value !== '') {
      setShowNameErrorState(false);
    }
    setPromptGroupInEdit((prev) => ({ ...prev, [key]: value }) as PromptGroup);
  }, []);

  useEffect(() => {
    if (!promptGroupInEdit && promptGroup.id === '') {
      setPromptGroupInEdit(promptGroup);
    }
  }, [promptGroup, promptGroupInEdit]);

  const onConfirm = useCallback(
    async (_promptGroup: PromptGroup) => {
      const isNewCard = _promptGroup.id === '';
      const isPristine = isEqual(_promptGroup, initialCardState);

      if (!isPristine) {
        if (isNewCard) {
          const isCardEmpty = Object.values(_promptGroup).every(
            (value) => value === '' || (Array.isArray(value) && value?.length === 0),
          );

          if (isCardEmpty) {
            removeEmptyPromptGroup();
            return;
          }

          if (_promptGroup.name === '') {
            setShowNameErrorState(true);
            return;
          }

          setIsCardLoading(true);
          await createPromptGroup(_promptGroup);
        }

        if (!isNewCard) {
          if (_promptGroup.name === '') {
            setShowNameErrorState(true);
            return;
          }
          setIsCardLoading(true);
          await updatePromptGroup(_promptGroup);
        }
      }

      setPromptGroupInEdit(null);
      setShowNameErrorState(false);
      setIsCardLoading(false);
    },
    [createPromptGroup, updatePromptGroup, removeEmptyPromptGroup, initialCardState],
  );

  const onDeleteCard = useCallback(
    async (promptGroup: PromptGroup) => {
      await removePromptGroup(promptGroup.id);
      setPromptGroupInEdit(null);
      setShowNameErrorState(false);
    },
    [removePromptGroup],
  );

  return {
    promptGroupInEdit,
    isCardLoading,
    showNameErrorState,
    setPromptGroupInEdit,
    onEdit,
    onConfirm,
    onDeleteCard,
  };
};
